import React, { useState, useEffect, useRef } from 'react';
import '../../styles/teacher/LeftSidebar.css';
import { fetchTeacherProfile } from '../../utils/teacherService';
import images from '../../assets/images';
import { supabase } from '../../utils/supabase';
import { getApiUrl } from '../../utils/apiConfig';

const LeftSidebar = () => {
  // State for teacher profile data
  const [teacherProfile, setTeacherProfile] = useState({
    fullName: 'Teacher',
    schoolName: 'Loading...',
    role: 'Loading...',
    subject: 'Loading...',
    email: ''
  });
  const [loading, setLoading] = useState(true);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [messageText, setMessageText] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(() => {
    try {
      return localStorage.getItem('teacherProfilePhoto') || '';
    } catch (e) {
      return '';
    }
  });
  const photoInputRef = useRef(null);

  // Center-crop the chosen image to a square and shrink it so it fills the circle
  // and stays small enough to keep in localStorage
  const resizeImageToSquare = (file, size = 500) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        const img = new Image();
        img.onerror = reject;
        img.onload = () => {
          const side = Math.min(img.width, img.height);
          const sx = (img.width - side) / 2;
          const sy = (img.height - side) / 2;
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          canvas.getContext('2d').drawImage(img, sx, sy, side, side, 0, 0, size, size);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });

  const handlePhotoChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please choose an image file.');
      return;
    }
    try {
      const dataUrl = await resizeImageToSquare(file);
      setProfilePhoto(dataUrl);
      try {
        localStorage.setItem('teacherProfilePhoto', dataUrl);
      } catch (storageError) {
        console.warn('⚠️ Could not save profile photo to localStorage:', storageError);
      }
    } catch (error) {
      console.error('❌ Failed to load profile photo:', error);
      alert('Could not load that image. Please try another one.');
    }
  };

  // Fetch teacher profile data on component mount
  useEffect(() => {
    const loadTeacherProfile = async () => {
      try {
        console.log('🔄 Loading teacher profile...');
        
        // Get user ID from Supabase session
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          console.log('👤 Fetching profile for Supabase user:', user.id);
          console.log('📧 User email:', user.email);
          console.log('👤 User metadata:', user.user_metadata);
          
          // Debug: Check if this user ID exists in localStorage from signup
          const signupUserId = localStorage.getItem('signupUserId');
          const currentUserId = localStorage.getItem('currentUserId');
          console.log('🔍 Debug - signupUserId from localStorage:', signupUserId);
          console.log('🔍 Debug - currentUserId from localStorage:', currentUserId);
          console.log('🔍 Debug - Supabase user.id:', user.id);
          
          // Try multiple user IDs if they don't match
          let profile = await fetchTeacherProfile(user.id, user.email);
          
          if (!profile && signupUserId && signupUserId !== user.id) {
            console.log('🔄 Trying with signupUserId as fallback...');
            profile = await fetchTeacherProfile(signupUserId, user.email);
          }
          
          if (!profile && currentUserId && currentUserId !== user.id) {
            console.log('🔄 Trying with currentUserId as fallback...');
            profile = await fetchTeacherProfile(currentUserId, user.email);
          }
          
          if (profile) {
            setTeacherProfile(profile);
            console.log('✅ Teacher profile loaded from backend:', profile);
            
            // Verify it was saved to localStorage
            const savedProfile = localStorage.getItem('teacherProfileData');
            if (savedProfile) {
              console.log('✅ Confirmed: Profile is now in localStorage');
            } else {
              console.warn('⚠️ Warning: Profile was not saved to localStorage');
            }
          } else {
            console.warn('⚠️ Teacher profile not found in backend');
            console.log('💡 Possible reasons:');
            console.log('   1. Teacher has not completed profile setup yet');
            console.log('   2. Teacher profile was saved with a different user_id');
            console.log('   3. Database connection issue');
            console.log('🔍 Checking localStorage for cached profile data...');
            
            // Try to get from localStorage片的 fallback
            const cachedProfile = localStorage.getItem('teacherProfileData');
            console.log('📦 localStorage teacherProfileData:', cachedProfile);
            
            if (cachedProfile) {
              try {
                const parsedProfile = JSON.parse(cachedProfile);
                setTeacherProfile(parsedProfile);
                console.log('✅ Loaded teacher profile from localStorage cache:', parsedProfile);
              } catch (e) {
                console.error('底 Failed to parse cached profile:', e);
              }
            } else {
              console.warn('⚠️ No cached profile data found in localStorage');
              console.log('📝 Teacher needs to complete profile setup at /teacher-profile-form');
              console.log('📊 Current localStorage keys:', Object.keys(localStorage));
            }
          }
        } else {
          console.warn('⚠️ No user session found');
        }
      } catch (error) {
        console.error('❌ Error loading teacher profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTeacherProfile();
  }, []);

  // Sample student data
  const students = [
    { id: 1, name: 'Alice Johnson', grade: 'A-', avatar: '👩‍🦰' },
    { id: 2, name: 'Bob Smith', grade: 'B+', avatar: '👨‍🦱' },
    { id: 3, name: 'Carol Davis', grade: 'A', avatar: '👩‍🦳' },
    { id: 4, name: 'David Wilson', grade: 'B', avatar: '👨‍🦲' },
    { id: 5, name: 'Emma Brown', grade: 'A+', avatar: '👩‍🦱' },
    { id: 6, name: 'Frank Miller', grade: 'C+', avatar: '👨‍🦰' }
  ];

  const handleMessageClick = () => {
    // TODO: Re-enable Student Chat functionality
    // setShowMessageModal(true);
    console.log('Student Chat button clicked (functionality disabled)');
  };

  const handleSendMessage = () => {
    if (selectedStudent && messageText.trim()) {
      console.log('Sending message to:', selectedStudent, 'Message:', messageText);
      // Here you would integrate with your messaging system
      alert(`Message sent to ${selectedStudent}!`);
      setMessageText('');
      setSelectedStudent('');
      setShowMessageModal(false);
    } else {
      alert('Please select a student and enter a message.');
    }
  };

  const handleCloseModal = () => {
    setShowMessageModal(false);
    setMessageText('');
    setSelectedStudent('');
  };

  // Debug function - can be called from browser console
  window.debugTeacherProfile = async () => {
    console.log('=== TEACHER PROFILE DEBUG ===');
    const { data: { user } } = await supabase.auth.getUser();
    console.log('1. Current Supabase user:', user?.id, user?.email);
    console.log('2. signupUserId:', localStorage.getItem(' augupUserId'));
    console.log('3. currentUserId:', localStorage.getItem('currentUserId'));
    console.log('4. teacherProfileData:', localStorage.getItem('teacherProfileData'));
    
    // Test with the known working user ID
    console.log('5. Testing with known working user ID...');
    const response = await fetch(getApiUrl('teacher-profile?user_id=8149f44b-56bd-479c-8538-e46fa7cb65fa'));
    const data = await response.json();
    console.log('6. Known working profile:', data);
    
    // Test with current Supabase user ID
    if (user?.id) {
      console.log('7. Testing with current Supabase user ID...');
      const response2 = await fetch(getApiUrl(`teacher-profile?user_id=${user.id}`));
      const data2 = await response2.json();
      console.log('8. Current user profile:', data2);
    }
  };

  return (
    <div className="left-sidebar">
      <div className="teacher-profile">
        <div
          className="profile-photo editable"
          style={{ backgroundColor: '#f0f0f0' }}
          onClick={() => photoInputRef.current && photoInputRef.current.click()}
          title="Change profile photo"
        >
          <img 
            src={profilePhoto || images.avatars.owlTeacher}
            alt="Teacher Profile" 
            className="profile-image"
          />
          <div className="profile-photo-overlay">Change Photo</div>
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handlePhotoChange}
          />
        </div>
        <div className="profile-info">
          <h3 className="teacher-name">{loading ? 'Loading...' : teacherProfile.fullName}</h3>
          <p className="teacher-subject">{teacherProfile.subject} {teacherProfile.role ? `- ${teacherProfile.role}` : ''}</p>
          <p className="teacher-status">Online</p>
        </div>
      </div>

      <div className="dropdown-section">
        <button className="education-button app-btn" onClick={handleMessageClick}>
          💬 Student Chat
        </button>
      </div>

      {/* Message Modal */}
      {showMessageModal && (
        <div className="message-modal-overlay">
          <div className="message-modal">
            <div className="modal-header">
              <h3>Send Message to Student</h3>
              <button className="close-button" onClick={handleCloseModal}>×</button>
            </div>
            
            <div className="modal-content">
              <div className="form-group">
                <label>Select Student:</label>
                <select 
                  value={selectedStudent} 
                  onChange={(e) => setSelectedStudent(e.target.value)}
                  className="student-select"
                >
                  <option value="">Choose a student...</option>
                  {students.map(student => (
                    <option key={student.id} value={student.name}>
                      {student.avatar} {student.name} (Grade: {student.grade})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Message:</label>
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Type your message here..."
                  className="message-textarea"
                  rows="4"
                />
              </div>

              <div className="modal-buttons">
                <button className="cancel-button app-btn" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button className="send-button app-btn" onClick={handleSendMessage}>
                  Send Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeftSidebar;